import { generateInvoice, generatePDFUPO } from '@akmf/ksef-fe-invoice-converter';
import { AdditionalData, PREVIEW_DEFAULTS, XmlInput } from './types';

/**
 * Rdzeń biblioteki — w 100% agnostyczny frameworkowo (działa w Angularze,
 * w czystym HTML, w każdym bundlerze). Opakowuje oficjalny generator MF
 * i sprowadza go do dwóch rzeczy, których realnie potrzebujesz do podglądu:
 * wygenerowanego PDF (Blob/base64) oraz gotowego do wpięcia w <iframe> URL-a.
 */

/** Normalizuje wejście (File | Blob | string XML) do obiektu `File`, którego oczekuje generator. */
export function toXmlFile(input: XmlInput, fileName = 'document.xml'): File {
  if (typeof File !== 'undefined' && input instanceof File) return input;
  const parts = typeof input === 'string' ? [input] : [input];
  return new File(parts as BlobPart[], fileName, { type: 'text/xml' });
}

/** Faktura (FA1/FA2/FA3/FA_RR) → Blob PDF. Wersja schemy wykrywana automatycznie. */
export function generateInvoicePdf(input: XmlInput, data: AdditionalData): Promise<Blob>;
/** Faktura → base64 (np. do `data:` URL, postMessage, zapisu). */
export function generateInvoicePdf(input: XmlInput, data: AdditionalData, format: 'base64'): Promise<string>;
export function generateInvoicePdf(input: XmlInput, data: AdditionalData, format: 'blob'): Promise<Blob>;
export function generateInvoicePdf(
  input: XmlInput,
  data: AdditionalData,
  format: 'blob' | 'base64' = 'blob',
): Promise<Blob | string> {
  const file = toXmlFile(input);
  return format === 'base64'
    ? generateInvoice(file, data, 'base64')
    : generateInvoice(file, data, 'blob');
}

/** UPO (Urzędowe Poświadczenie Odbioru, v4_2/4_3) → Blob PDF. */
export function generateUpoPdf(input: XmlInput): Promise<Blob> {
  return generatePDFUPO(toXmlFile(input, 'upo.xml'));
}

/**
 * Faktura → obiektowy URL gotowy do wstawienia w `<iframe src>`.
 * UWAGA: wywołujący jest właścicielem URL-a i musi go zwolnić przez {@link revokePreviewUrl},
 * gdy podgląd znika (inaczej przeciek pamięci na każdym wyrenderowaniu).
 */
export async function createInvoicePreviewUrl(
  input: XmlInput,
  data: AdditionalData = PREVIEW_DEFAULTS,
): Promise<string> {
  const blob = await generateInvoicePdf(input, data, 'blob');
  return URL.createObjectURL(blob);
}

/** UPO → obiektowy URL do `<iframe src>`. Zwolnij przez {@link revokePreviewUrl}. */
export async function createUpoPreviewUrl(input: XmlInput): Promise<string> {
  const blob = await generateUpoPdf(input);
  return URL.createObjectURL(blob);
}

/** Zwalnia URL utworzony przez `create*PreviewUrl`. Bezpieczne dla `null`/pustych. */
export function revokePreviewUrl(url: string | null | undefined): void {
  if (url) URL.revokeObjectURL(url);
}

// Furtka dla zaawansowanych: niskopoziomowe generatory zwracające obiekt pdfmake.
export {
  generateInvoice,
  generatePDFUPO,
} from '@akmf/ksef-fe-invoice-converter';

export type { AdditionalData, XmlInput, DocumentKind } from './types';
export { PREVIEW_DEFAULTS } from './types';
